import React from 'react';
import { Form, Input, Select, Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { FormInstance } from 'antd/lib/form';
import cls from 'classnames';
import intl from 'react-intl-universal';

import { renderContent } from '@/utils';
import { format2FullTimeStr } from '@/utils/date';
import { OCC_TYPE_MAP, OccType } from '@/config/constant';
import IconFont from '@/components/IconFont';
import ContentBlock, { FormItem } from '@/components/Reservation/ContentBlock';
import { formatAreaMobileStr } from '@/components/BaseMobileInput/helper';

import styles from './index.module.scss';

// 创建新预订
export const CreateNewOptionSymbol = 'CREATE_NEW_RENEWAL';
export const OptionAllSymbol = -99;

interface SubOrderInfo {
  recNo: string;
  roomTypeName: string;
  roomNo: string;
  sourceChannelName: string;
  occType: OccType;
  expectedArrivalTime: number;
  expectedDepartureTime: number;
}

export interface OrderOptionItem {
  resvNo: string;
  name: string;
  mobile: string;
  otaOrderNo: string;
  email: string;
  subOrders: SubOrderInfo[];
}

interface IProps {
  form: FormInstance;
  formOption: any;
  value: string;
  dataSource: OrderOptionItem[];
  onChange: (val) => void;
  onSearch: () => void;
}

// 表格列
const TableColumn: ColumnProps<SubOrderInfo>[] = [
  { title: intl.get('app_constants_room_type'), dataIndex: 'roomTypeName' },
  {
    title: intl.get('app_constants_room_no'),
    dataIndex: 'roomNo',
    render: (val) => val || intl.get('app_reservation_room_queue_no'),
  },
  { title: intl.get('app_reservation_channel_name'), dataIndex: 'sourceChannelName' },
  {
    title: intl.get('app_reservation_channel_no'),
    dataIndex: 'channelOrderNo',
    render: (val) => renderContent(val),
  },
  {
    title: intl.get('app_reservation_occ_type'),
    dataIndex: 'occType',
    render: (val) => OCC_TYPE_MAP[val],
  },
  {
    title: intl.get('app_reservation_eta_time'),
    dataIndex: 'expectedArrivalTime',
    render: format2FullTimeStr,
  },
  {
    title: intl.get('app_reservation_etd_time'),
    dataIndex: 'expectedDepartureTime',
    render: format2FullTimeStr,
  },
];

const Index: React.FC<IProps> = (props) => {
  const { form, value, onChange, onSearch, dataSource, formOption } = props;

  // 选中创建
  const handleSelectCreate = () => {
    if (value !== CreateNewOptionSymbol) {
      onChange(CreateNewOptionSymbol);
    }
  };

  // 选中订单
  const handleSelectOption = (record: OrderOptionItem) => {
    if (record.resvNo !== value) {
      onChange(record.resvNo);
    }
  };

  // 渲染订单
  const renderOptionList = () => {
    return dataSource.map((item) => {
      const { resvNo, name, mobile, email, otaOrderNo } = item;
      return (
        <li
          key={resvNo}
          className={cls(
            styles.orderOptionItem,
            value === resvNo ? styles.checkedOptionItem : null,
          )}
          onClick={() => handleSelectOption(item)}
        >
          <div>
            <FormItem label={intl.get('app_constants_booker_name')}>{name}</FormItem>
            <FormItem label={intl.get('app_reservation_phone_number')}>
              {renderContent(mobile, formatAreaMobileStr)}
            </FormItem>
            <FormItem label={intl.get('reservation_booker_field_ota_virtual_label')}>
              {renderContent(otaOrderNo)}
            </FormItem>
            <FormItem label={intl.get('reservation_booker_field_email_placeholder')}>
              {renderContent(email)}
            </FormItem>
          </div>
          <Table
            rowKey="recNo"
            columns={TableColumn}
            dataSource={item.subOrders || []}
            pagination={false}
          />
          <IconFont
            type="icon-a-84gouxuan-youxiajiaogouxuan1"
            className={styles.checkedOptionIcon}
          />
        </li>
      );
    });
  };

  return (
    <ContentBlock
      title={intl.get('drawer_extension_picker_extension_method')}
      className={styles.methodInfoBlock}
    >
      <div className={styles.methodHintText}>{intl.get('drawer_extension_picker_option_hint')}</div>
      <div className={styles.searchForm}>
        <Form layout="inline" form={form}>
          <Form.Item label={intl.get('reservation_booker_block_title')} name="keyWord">
            <Input
              placeholder={intl.get('drawer_extension_picker_booker_placeholder')}
              onBlur={onSearch}
            />
          </Form.Item>
          <Form.Item
            label={intl.get('app_reservation_channel_name')}
            name="channelValue"
            initialValue={OptionAllSymbol}
          >
            <Select options={formOption.channelList} onChange={onSearch} />
          </Form.Item>
        </Form>
      </div>
      <ul className={styles.methodOptionList}>
        <li
          className={cls(
            styles.createOptionItem,
            value === CreateNewOptionSymbol ? styles.checkedOptionItem : null,
          )}
          onClick={handleSelectCreate}
        >
          <IconFont type="icon-chuangjian" className={styles.createOptionIcon} />
          <span>{intl.get('drawer_extension_picker_create_new')}</span>
          <IconFont
            type="icon-a-84gouxuan-youxiajiaogouxuan1"
            className={styles.checkedOptionIcon}
          />
        </li>
        {renderOptionList()}
      </ul>
    </ContentBlock>
  );
};

export default Index;
